import { RefObject, useEffect } from "react";
import { Chart, Axis } from "highcharts";

export const useDynamicXAxisStep = (
  chartApi: Chart | null,
  containerRef: RefObject<HTMLElement>,
  dataLength: number,
  estimatedLabelWidth: number = 101,
  firstRenderCallback?: () => void,
) => {
  useEffect(() => {
    try {
      if (!chartApi || !containerRef.current) return;

      const updateXAxisStep = () => {
        const containerWidth = containerRef?.current?.offsetWidth ?? 0;
        const maxLabels = Math.floor(containerWidth / estimatedLabelWidth);

        // Ensure we show at least 2 labels (first and last)
        const visibleLabelsCount = Math.max(2, maxLabels > 0 ? maxLabels : 2);

        chartApi?.xAxis?.at(0)?.update?.({ labels: { enabled: true } });

        // Update the tick positioner function
        chartApi?.xAxis?.at(0)?.update?.({
          tickPositioner: function (this: Axis): number[] {
            return calculateTickPositions(dataLength, visibleLabelsCount);
          },
        });
      };

      updateXAxisStep();
      firstRenderCallback?.();

      const observer = new ResizeObserver(() => {
        updateXAxisStep();
        const chartExists = !!Object.keys(chartApi ?? {}).length;

        if (chartExists) {
          chartApi?.reflow?.();
        }
      });
      observer.observe(containerRef.current);

      return () => observer.disconnect();
    } catch (e) {
      console.error(`useDynamicXAxisStep error message: ${e}`);
    }
  }, [
    chartApi,
    containerRef,
    dataLength,
    estimatedLabelWidth,
    firstRenderCallback,
  ]);
};

// This function returns an array of tick indexes which need to be displayed on the X-axis.
const calculateTickPositions = (
  dataLength: number,
  visibleLabelsCount: number,
): number[] => {
  const positions: number[] = [];

  // Always show the first position (index 0)
  positions.push(0);

  // If we have space for more than 2 labels, calculate intermediate positions
  if (visibleLabelsCount > 2) {
    // Calculate step size to distribute labels evenly
    const step = Math.ceil(dataLength / (visibleLabelsCount - 1));

    // Check if we have enough space to show all data points
    const hasEnoughSpace = visibleLabelsCount >= dataLength;

    // Add intermediate positions at regular intervals
    for (let i = step; i < dataLength - 1; i += step) {
      // Skip the second-to-last position if we don't have enough space
      // This prevents overcrowding near the end of the axis
      if (!hasEnoughSpace && dataLength > 2 && i === dataLength - 2) {
        continue;
      }
      positions.push(i);
    }
  }

  // Always show the last position (if we have more than one data point)
  if (dataLength > 1) {
    positions.push(dataLength - 1);
  }

  return positions;
};
